import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEye, FiEyeOff } from 'react-icons/fi';
import { Button, Input, PasswordStrengthMeter } from '../../components/ui';
import { useAuth } from '../../contexts';
import { usePreferences } from '../../contexts/PreferencesContext';
import logoClaro from '../../assets/Logo_claro.png';
import logoEscuro from '../../assets/Logo_escuro.png';
import { formatPhoneAsYouType, isPasswordStrongEnough, isValidEmail, isValidName, isValidPhone } from '../../utils/validation';
import styles from './Register.module.css';

type Translate = (key: string, vars?: Record<string, string>) => string;
type FieldName = 'name' | 'email' | 'whatsapp' | 'password' | 'confirmPassword';

function onlyDigits(value: string): string {
    return value.replace(/\D/g, '');
}

function getNameError(value: string, t: Translate): string | null {
    if (!value.trim()) return t('register.erroNomeObrigatorio');
    if (!isValidName(value)) return t('register.erroNomeInvalido');
    return null;
}

function getEmailError(value: string, t: Translate): string | null {
    if (!value.trim()) return t('register.erroEmailObrigatorio');
    if (!isValidEmail(value)) return t('register.erroEmailInvalido');
    return null;
}

function getWhatsappError(value: string, t: Translate): string | null {
    const digits = onlyDigits(value);
    if (!digits) return t('register.erroWhatsappObrigatorio');
    if (digits.length < 10 || digits.length > 15) return t('register.erroWhatsapp');
    if (!isValidPhone(digits)) return t('register.erroWhatsappInvalido');
    return null;
}

function getPasswordError(value: string, t: Translate): string | null {
    if (!value) return t('register.erroSenhaObrigatoria');
    if (!isPasswordStrongEnough(value)) return t('register.erroSenhaFraca');
    return null;
}

function getConfirmPasswordError(password: string, confirm: string, t: Translate): string | null {
    if (!confirm) return t('register.erroConfirmarSenhaObrigatoria');
    if (password !== confirm) return t('register.erroSenhasCoincidem');
    return null;
}

export function Register() {
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [whatsapp, setWhatsapp] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [touched, setTouched] = useState<Record<FieldName, boolean>>({
        name: false,
        email: false,
        whatsapp: false,
        password: false,
        confirmPassword: false,
    });

    const { register, isLoading } = useAuth();
    const navigate = useNavigate();
    const { t, prefs } = usePreferences();
    const darkMode = prefs.darkMode;

    const errors = useMemo(
        () => ({
            name: getNameError(name, t),
            email: getEmailError(email, t),
            whatsapp: getWhatsappError(whatsapp, t),
            password: getPasswordError(password, t),
            confirmPassword: getConfirmPasswordError(password, confirmPassword, t),
        }),
        [name, email, whatsapp, password, confirmPassword, t]
    );

    const isFormValid =
        !errors.name && !errors.email && !errors.whatsapp && !errors.password && !errors.confirmPassword;

    const markTouched = (field: FieldName) => {
        setTouched((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        setSubmitError('');
        setTouched({ name: true, email: true, whatsapp: true, password: true, confirmPassword: true });

        if (!isFormValid) return;

        try {
            await register({
                name: name.trim(),
                email: email.trim(),
                password,
                whatsapp: onlyDigits(whatsapp),
            });
        } catch (err: any) {
            const msg = err?.message || '';
            if (msg.toLowerCase().includes('already exists') || msg.toLowerCase().includes('email')) {
                setSubmitError(t('register.erroEmailExiste'));
            } else {
                setSubmitError(msg || t('register.erroGenerico'));
            }
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.card}>
                <div className={styles.logo}>
                    <img
                        src={darkMode ? logoClaro : logoEscuro}
                        alt="Financeiro Diário"
                        className={styles.logoImg}
                    />
                </div>

                <div className={styles.header}>
                    <h1 className={styles.title}>{t('register.titulo')}</h1>
                    <p className={styles.subtitle}>{t('register.subtitulo')}</p>
                </div>

                <form className={styles.form} onSubmit={handleSubmit} noValidate>
                    {submitError && <div className={styles.error}>{submitError}</div>}

                    <Input
                        type="text"
                        label={t('register.nomeCompleto')}
                        placeholder="João Silva"
                        autoComplete="name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={() => markTouched('name')}
                        error={touched.name && errors.name ? errors.name : undefined}
                        aria-invalid={Boolean(touched.name && errors.name)}
                        fullWidth
                    />

                    <Input
                        type="email"
                        label={t('register.email')}
                        placeholder={t('register.emailPlaceholder')}
                        autoComplete="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        onBlur={() => markTouched('email')}
                        error={touched.email && errors.email ? errors.email : undefined}
                        aria-invalid={Boolean(touched.email && errors.email)}
                        fullWidth
                    />

                    <div>
                        <Input
                            type="tel"
                            label={t('register.whatsapp')}
                            placeholder="+55 11 99999-8888"
                            autoComplete="tel"
                            maxLength={20}
                            value={formatPhoneAsYouType(whatsapp)}
                            onChange={(e) => setWhatsapp(onlyDigits(e.target.value).slice(0, 15))}
                            onBlur={() => markTouched('whatsapp')}
                            error={touched.whatsapp && errors.whatsapp ? errors.whatsapp : undefined}
                            aria-invalid={Boolean(touched.whatsapp && errors.whatsapp)}
                            fullWidth
                        />
                        {!(touched.whatsapp && errors.whatsapp) && (
                            <p className={styles.hint}>{t('register.whatsappHint')}</p>
                        )}
                    </div>

                    <div>
                        <Input
                            type={showPassword ? 'text' : 'password'}
                            label={t('register.senha')}
                            placeholder="••••••••"
                            autoComplete="new-password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            onBlur={() => markTouched('password')}
                            error={touched.password && !password ? errors.password ?? undefined : undefined}
                            aria-invalid={Boolean(touched.password && errors.password)}
                            rightElement={
                                <button
                                    type="button"
                                    className={styles.toggleVisibility}
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    aria-label={showPassword ? t('register.ocultarSenha') : t('register.mostrarSenha')}
                                >
                                    {showPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                                </button>
                            }
                            fullWidth
                        />
                        {password.length > 0 && <PasswordStrengthMeter password={password} />}
                    </div>

                    <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        label={t('register.confirmarSenha')}
                        placeholder="••••••••"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        onBlur={() => markTouched('confirmPassword')}
                        error={touched.confirmPassword && errors.confirmPassword ? errors.confirmPassword : undefined}
                        aria-invalid={Boolean(touched.confirmPassword && errors.confirmPassword)}
                        rightElement={
                            <button
                                type="button"
                                className={styles.toggleVisibility}
                                onClick={() => setShowConfirmPassword((prev) => !prev)}
                                aria-label={
                                    showConfirmPassword ? t('register.ocultarSenha') : t('register.mostrarSenha')
                                }
                            >
                                {showConfirmPassword ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                            </button>
                        }
                        fullWidth
                    />

                    <Button type="submit" fullWidth isLoading={isLoading} disabled={!isFormValid}>
                        {t('register.criar')}
                    </Button>
                </form>

                <p className={styles.footer}>
                    {t('register.temConta')}{' '}
                    <button
                        type="button"
                        className={styles.footerLink}
                        onClick={() => navigate('/login')}
                    >
                        {t('register.entrar')}
                    </button>
                </p>
            </div>
        </div>
    );
}
